/**
 * SuperBrain Pricing — 4-tier membership conversion page
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 */

import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './PricingPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const TIERS = [
  {
    id: 'professional',
    name: 'Professional',
    monthly: 59,
    yearly: 590,
    yearlySavings: 118,
    tagline: 'For individuals who want to review answers with citations',
    badge: null,
    features: [
      '225 queries per month',
      'Full 7-AI Council (6 specialists + Master Synthesizer)',
      'Inline citations & dissent flagging',
      'Citation Review Mode',
      'Agentic tools (web search, file upload)',
      'Conversation memory & history',
      'PDF / document upload',
      '25 image generations / month',
      'Priority processing speed',
      'Export conversations',
      'Cancel anytime'
    ],
    cta: 'Start 3-Day Free Trial'
  },
  {
    id: 'business',
    name: 'Business',
    monthly: 99,
    yearly: 990,
    yearlySavings: 198,
    tagline: 'For teams & power users',
    badge: 'Most Popular',
    features: [
      'Everything in Professional, plus:',
      '400 queries per month',
      '75 image generations / month',
      'Live chat support'
    ],
    cta: 'Start 3-Day Free Trial'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 249,
    yearly: 2490,
    yearlySavings: 498,
    tagline: 'For organizations & heavy workloads',
    badge: null,
    features: [
      'Everything in Business, plus:',
      '1,000 queries per month',
      '250 image generations / month',
      'Advanced usage analytics',
      'SSO-ready account (on request)',
      'Dedicated onboarding call',
      '24-hour support response'
    ],
    cta: 'Start 3-Day Free Trial'
  },
  {
    id: 'ultimate',
    name: 'Ultimate',
    monthly: 499,
    yearly: 4990,
    yearlySavings: 998,
    tagline: 'Higher usage limits and account support',
    badge: null,
    features: [
      'Everything in Enterprise, plus:',
      '2,000 queries per month',
      '750 image generations / month',
      'Custom AI model weighting',
      'Priority access to new features',
      'Quarterly strategy review',
      '4-hour support response',
      'Account manager assigned'
    ],
    cta: 'Start 3-Day Free Trial'
  }
];

const FAQ = [
  {
    q: 'Why do I need a payment method for the free trial?',
    a: 'A payment method is collected at signup. The selected subscription begins automatically when the 3-day trial ends unless you cancel before the trial ends. There is no subscription charge when you cancel during the trial.'
  },
  {
    q: 'What happens after my 3-day free trial?',
    a: 'Your selected plan begins billing when the 3-day trial ends unless you cancel first. Cancel from your dashboard before the trial ends to avoid the first subscription charge. After billing begins, cancellation stops the next renewal and access continues through the current paid period.'
  },
  {
    q: 'What happens if I hit my monthly query cap?',
    a: 'You\'ll see a friendly notice with an upgrade option. Your conversations, documents, and settings remain accessible — only new query submissions are paused until the next billing cycle. Limits exist so we can keep the service fast and self-funded by memberships without ads or data sales.'
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your dashboard at any time. Cancellation stops future renewals; access continues through the end of the current paid period. If you cancel before the 3-day trial ends, no subscription charge is due.'
  },
  {
    q: 'How does SuperBrain use multiple AI providers?',
    a: 'SuperBrain can send a query to multiple configured AI model providers and present a synthesized response. Citations and points of disagreement may be shown when available. AI output can be wrong, so verify important information independently.'
  },
  {
    q: 'How does the referral reward work?',
    a: 'Share your private invite link from the dashboard. When a friend subscribes to any paid plan, we add 1 week of free service to your subscription automatically. No cap.'
  },
  {
    q: 'Is my data sold or used to train AI?',
    a: 'We do not sell your data. Prompts are routed through OpenRouter to selected AI model providers; their handling is described in our Privacy Policy.'
  },
  {
    q: 'Can I switch plans later?',
    a: 'Anytime. Upgrades take effect immediately with prorated charges. Downgrades take effect at your next billing cycle.'
  },
  {
    q: 'Do you offer refunds?',
    a: 'Refund eligibility depends on the billing cadence: monthly plans may qualify for a full refund request within 7 days of a charge if usage is at most 1,000 queries; annual plans may qualify for a prorated refund request within 30 days of purchase. See the Refund Policy for details.'
  }
];

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const handleUpgrade = async (planId) => {
    setLoadingPlan(planId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }
      const planSlug = `${planId}_${billingCycle}`;
      const response = await axios.post(
        `${BACKEND_URL}/api/subscriptions/create-checkout`,
        {
          plan: planSlug,
          success_url: `${window.location.origin}/dashboard?upgrade=success`,
          cancel_url: `${window.location.origin}/pricing?upgrade=cancelled`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Checkout is being prepared — please try again shortly or email us.';
      alert(msg);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="pricing-page" data-testid="pricing-page">
      <div className="pricing-header">
        <a href="/" className="pricing-back" data-testid="pricing-back-link">← Back</a>
        <h1>Choose Your SuperBrain Plan</h1>
        <p className="subtitle">
          6 AI models. One synthesized answer with citations. One transparent membership.
        </p>
        <div className="accuracy-banner" data-testid="pricing-accuracy-banner">
          Multiple model responses · Citations when available · Disagreement indicators
        </div>

        {/* Core workflow summary */}
        <div className="ferrari-banner" data-testid="ferrari-banner">
          <div className="ferrari-banner-icon">★</div>
          <div className="ferrari-banner-body">
            <div className="ferrari-banner-title">The core AI Council is included on every plan.</div>
            <div className="ferrari-banner-sub">
              Plans include the AI Council and synthesized responses. Usage limits and additional features vary by plan.
            </div>
            <div className="ferrari-banner-note" data-testid="builder-disclosure">
              <strong>What you get:</strong> AI Council queries · image generation · memory · export · agentic web tools. <span style={{ opacity: 0.75 }}>SuperBrain Builder (one-shot full-stack codegen) is an internal admin tool and is not included in any customer tier.</span>
            </div>
          </div>
        </div>

        {/* Describe SuperBrain's workflow without unsupported competitor comparisons. */}
        <div className="compare-banner" data-testid="compare-banner">
          <div className="compare-banner-title">How SuperBrain works</div>
          <div className="compare-banner-sub">
            A multi-provider workflow with answers for you to review.
          </div>
          <div className="compare-table-wrap">
            <table className="compare-table" data-testid="compare-table">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>What happens</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Ask</td>
                  <td>Your question is sent to configured AI model providers.</td>
                </tr>
                <tr>
                  <td>Review</td>
                  <td>SuperBrain presents a synthesized response from available model replies.</td>
                </tr>
                <tr>
                  <td>Check sources</td>
                  <td>Citations and differences may be included when available; check important claims yourself.</td>
                </tr>
                <tr>
                  <td>Provider availability</td>
                  <td>Available models and providers can change over time.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="trust-strip" data-testid="trust-strip">
          <span>✓ 3-day free trial</span>
          <span>✓ Cancel anytime</span>
          <span>✓ We do not sell user data</span>
          <span>✓ Self-funded by memberships only</span>
        </div>

        <div className="billing-toggle" data-testid="billing-toggle">
          <button
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
            data-testid="toggle-monthly"
          >
            Monthly
          </button>
          <button
            className={billingCycle === 'yearly' ? 'active' : ''}
            onClick={() => setBillingCycle('yearly')}
            data-testid="toggle-yearly"
          >
            Yearly <span className="save-badge">Save 2 months</span>
          </button>
        </div>
      </div>

      <section className="bundle-offer" data-testid="bundle-offer" aria-labelledby="bundle-offer-title">
        <h2 id="bundle-offer-title">Two-app subscription offer</h2>
        <p>
          Using 2 of our apps? Save 15% with code BUNDLE2 when you subscribe to both SuperBrain and VeteranClaimAid.
        </p>
        <p>
          The prices shown are the full prices before the discount: Professional $59/month or $590/year;
          Business $99/month or $990/year; Enterprise $249/month or $2,490/year; Ultimate $499/month
          or $4,990/year.
        </p>
        <p>
          The discount applies while both subscriptions stay active; we may remove it if one is cancelled.
          Each subscription renews automatically until cancelled.
        </p>
        <p>Bundle discounts cannot be combined with other discounts or promotion codes.</p>
      </section>

      <div className="pricing-cards-grid" data-testid="pricing-cards-grid">
        {TIERS.map((tier) => (
          <div
            key={tier.id}
            className={`pricing-card tier-${tier.id} ${tier.badge ? 'featured' : ''}`}
            data-testid={`tier-${tier.id}-card`}
          >
            {tier.badge && <div className="tier-badge popular">{tier.badge}</div>}
            <h2>{tier.name}</h2>
            <p className="tier-tagline">{tier.tagline}</p>

            <div className="price">
              {billingCycle === 'monthly' ? (
                <>
                  <span className="amount" data-testid={`tier-${tier.id}-price`}>${tier.monthly}</span>
                  <span className="period">/ month</span>
                </>
              ) : (
                <>
                  <span className="amount" data-testid={`tier-${tier.id}-price`}>${tier.yearly}</span>
                  <span className="period">/ year</span>
                  <div className="savings">Save ${tier.yearlySavings} vs monthly</div>
                </>
              )}
            </div>

            <ul className="features">
              {tier.features.map((f, i) => (
                <li key={i} className="included">{f.startsWith('Everything') ? <strong>{f}</strong> : <>✓ {f}</>}</li>
              ))}
            </ul>

            <button
              className={`btn-select ${tier.badge ? 'pro-button' : ''}`}
              onClick={() => handleUpgrade(tier.id)}
              disabled={loadingPlan === tier.id}
              data-testid={`tier-${tier.id}-cta`}
            >
              {loadingPlan === tier.id ? 'Preparing checkout...' : tier.cta}
            </button>

            <div className="trial-note">
              After the 3-day trial: <strong>${(billingCycle === 'monthly' ? tier.monthly : tier.yearly).toLocaleString('en-US')}/{billingCycle === 'monthly' ? 'month' : 'year'}</strong>, billed {billingCycle} and renewing automatically until canceled.
              Cancel anytime in your dashboard. Cancel before the trial ends to avoid the first charge; later cancellations stop the next renewal and keep access through the current paid period.
            </div>
          </div>
        ))}
      </div>

        <div className="value-comparison" data-testid="value-comparison">
          <h2>Choose a plan that fits your usage</h2>
          <p>
            Review each plan&apos;s current price, billing cadence, usage limits,
            and included features before subscribing. Provider availability and
            model selection can change over time.
          </p>
      </div>

      <div className="faq-section" data-testid="faq-section">
        <h2>Frequently Asked Questions</h2>
        {FAQ.map((item, i) => (
          <div
            key={i}
            className={`faq-item ${openFaq === i ? 'open' : ''}`}
            data-testid={`faq-item-${i}`}
          >
            <button
              className="faq-q"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              data-testid={`faq-toggle-${i}`}
            >
              <span>{item.q}</span>
              <span className="faq-arrow">{openFaq === i ? '−' : '+'}</span>
            </button>
            {openFaq === i && <p className="faq-a">{item.a}</p>}
          </div>
        ))}
      </div>

      <div className="pricing-footer">
        <p>
          <a href="/refund" data-testid="pricing-footer-refund">Refund Policy</a> ·{' '}
          <a href="/terms" data-testid="pricing-footer-terms">Terms</a> ·{' '}
          <a href="/privacy" data-testid="pricing-footer-privacy">Privacy</a> ·{' '}
          <a href="/disclaimer" data-testid="pricing-footer-disclaimer">Disclaimer</a> ·{' '}
          <a href="/dmca" data-testid="pricing-footer-dmca">DMCA</a>
        </p>
        <p>✝ GOD BLESS EVERYONE ✝</p>
        <p>© 2026 A-1 Affiliate Marketing LLC — All Rights Reserved</p>
      </div>
    </div>
  );
};

export default PricingPage;
